import { useCallback, useEffect, useRef, useState } from 'react';

const apiError = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
// ponytail: in-memory per tab, never persisted, cleared on logout/401; add size or TTL eviction if sessions get long.
const cache = new Map();
const refreshers = new Set();
const writers = new Map();

export const clearResourceCache = () => cache.clear();
export const refreshAll = () => refreshers.forEach((refresh) => refresh());

const sessionScope = () => {
  try {
    const payload = JSON.parse(atob(localStorage.getItem('token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId || payload.email || 'session';
  } catch {
    return 'anonymous';
  }
};

const scoped = (key) => `${sessionScope()}|${key}`;

export function setResource(key, data) {
  const cacheKey = scoped(key);
  cache.set(cacheKey, data);
  (writers.get(cacheKey) || []).forEach((write) => write(data));
}

export function useResource(key, fetcher, errorMessage = 'Could not load this', { keepPrevious = false } = {}) {
  const cacheKey = key === null ? null : scoped(key);
  const [state, setState] = useState(() => ({ data: cacheKey ? cache.get(cacheKey) : undefined, dataKey: cacheKey, error: null, loading: true }));
  const [attempt, setAttempt] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (cacheKey === null) return undefined;
    let alive = true;
    setState((s) => (cache.has(cacheKey)
      ? { data: cache.get(cacheKey), dataKey: cacheKey, error: null, loading: true }
      : { data: keepPrevious ? s.data : undefined, dataKey: keepPrevious ? s.dataKey : cacheKey, error: null, loading: true }));
    Promise.resolve()
      .then(() => fetcherRef.current())
      .then(
        (data) => {
          cache.set(cacheKey, data);
          if (alive) setState({ data, dataKey: cacheKey, error: null, loading: false });
        },
        (err) => alive && setState((s) => ({
          data: s.dataKey === cacheKey ? s.data : undefined,
          dataKey: cacheKey,
          error: apiError(err, errorMessage),
          loading: false,
        })),
      );
    return () => { alive = false; };
  }, [cacheKey, attempt, errorMessage, keepPrevious]);

  useEffect(() => {
    if (cacheKey === null) return undefined;
    const write = (data) => setState((s) => ({ ...s, data, dataKey: cacheKey, error: null }));
    if (!writers.has(cacheKey)) writers.set(cacheKey, new Set());
    writers.get(cacheKey).add(write);
    return () => writers.get(cacheKey).delete(write);
  }, [cacheKey]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    refreshers.add(reload);
    return () => refreshers.delete(reload);
  }, [reload]);

  const { dataKey, ...visible } = state;
  return { ...visible, reload };
}
